import { useState } from "react";
import { Box, Menu, MenuItem } from "@mui/material";
import { ChevronRight } from "lucide-mui";
import { CheckboxField } from "./ui";
import { styledComponent } from "../utils/styled";

const OptionsColumn = styledComponent(Box)(({ columnWidth }) => ({
  minWidth: columnWidth,
}));

const SubOptionsColumn = styledComponent(Box)(({ isDark }) => ({
  borderLeft: "1px solid",
  borderColor: isDark ? "#434950" : "#D5D9DF",
  paddingLeft: "5px",
  marginLeft: "5px",
}));

const OptionRow = styledComponent(MenuItem)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "14px",
  padding: "6px 8px",
  marginBottom: "5px",
  borderRadius: "8px",
  color: isDark ? "#D5D9DF" : "#434950",
  justifyContent: "space-between",
  gap: "8px",
  "&:hover": { backgroundColor: isDark ? "#31363B" : "#DFE3E9" },
  "&.Mui-selected": {
    backgroundColor: isDark ? "#434950" : "#D5D9DF",
    fontWeight: 500,
    "&:hover": { backgroundColor: isDark ? "#434950" : "#D5D9DF" },
  },
  "& .MuiTouchRipple-root": { display: "none" },
}));

const SubOptionRow = styledComponent(MenuItem)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "14px",
  padding: "6px 8px",
  marginBottom: "5px",
  borderRadius: "8px",
  color: isDark ? "#D5D9DF" : "#434950",
  gap: "8px",
  "&:hover": { backgroundColor: "transparent" },
  "& .MuiTouchRipple-root": { display: "none" },
}));

const SubmenuArrow = styledComponent(ChevronRight)(({ isDark }) => ({
  fontSize: "16px",
  color: isDark ? "#D5D9DF" : "#434950",
}));

const menuPaperSx = (isDark, anchorEl) => ({
  backgroundColor: isDark ? "#262A2E" : "#ECEFF3",
  marginTop: "5px",
  padding: "0 5px",
  paddingTop: "5px",
  borderRadius: "8px",
  border: "1px solid",
  borderColor: isDark ? "#434950" : "#D5D9DF",
  minWidth: anchorEl ? anchorEl.offsetWidth + 10 : "auto",
});

export function NestedFilterMenu({
  isDark,
  anchorEl,
  open,
  onClose,
  options,
  selectedValue,
  onSelect,
  selectedSubValues = [],
  onSubOptionToggle,
}) {
  const [openSubmenuFor, setOpenSubmenuFor] = useState(() => {
    const initial = options.find(o => o.value === selectedValue);
    return initial?.subOptions?.length ? selectedValue : null;
  });

  const activeOption = options.find(o => o.value === openSubmenuFor);
  const activeSubOptions = activeOption?.subOptions;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{ disablePadding: true, sx: { display: "flex" } }}
      slotProps={{ paper: { elevation: 3, sx: menuPaperSx(isDark, anchorEl) } }}
    >
      <OptionsColumn
        columnWidth={anchorEl ? anchorEl.offsetWidth + 10 : "auto"}
      >
        {options.map(option => {
          const hasSubOptions = !!option.subOptions?.length;
          return (
            <OptionRow
              key={option.value}
              isDark={isDark}
              selected={option.value === selectedValue}
              onClick={() => {
                if (hasSubOptions) return;
                onSelect(option.value);
              }}
              onMouseEnter={() =>
                setOpenSubmenuFor(hasSubOptions ? option.value : null)
              }
            >
              {option.label}
              {hasSubOptions && <SubmenuArrow isDark={isDark} />}
            </OptionRow>
          );
        })}
      </OptionsColumn>

      {!!activeSubOptions?.length && (
        <SubOptionsColumn
          isDark={isDark}
          onMouseEnter={() => setOpenSubmenuFor(activeOption.value)}
        >
          {activeSubOptions.map(subOption => (
            <SubOptionRow
              key={subOption.value}
              isDark={isDark}
              onClick={() => onSubOptionToggle(subOption.value)}
            >
              <CheckboxField
                isDark={isDark}
                size={18}
                checked={selectedSubValues.includes(subOption.value)}
                onChange={() => {}}
              />
              {subOption.label}
            </SubOptionRow>
          ))}
        </SubOptionsColumn>
      )}
    </Menu>
  );
}
